'use client';

import { Building03Icon, Tick02Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import * as React from 'react';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from './command';
import { DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger } from './dropdown-menu';

export interface ChangePartnerOption {
  /** The `partner_key` written to the cookie (e.g. "LOW"). */
  key: string;
  /** Display label — the `partner_display_name`, falling back to `partner_name`. */
  name: string;
}

export interface ChangePartnerMenuProps {
  /** All partners the admin can switch to. Sorted alphabetically internally. */
  partners: ChangePartnerOption[];
  /** The currently active `partner_key` (renders a tick). */
  activePartnerKey?: string | undefined;
  /** The `partner_key` treated as "Artic" and pinned first. */
  articPartnerKey?: string | undefined;
  /** The last non-Artic `partner_key` viewed, pinned second. */
  lastPartnerKey?: string | undefined;
  /** Called with the chosen `partner_key`. */
  onSelect: (partnerKey: string) => void;
}

function PartnerCommandItem({
  option,
  value,
  active,
  onSelect,
}: {
  option: ChangePartnerOption;
  value: string;
  active: boolean;
  onSelect: (partnerKey: string) => void;
}) {
  return (
    <CommandItem
      value={value}
      keywords={[option.name, option.key]}
      onSelect={() => onSelect(option.key)}
    >
      <span className="truncate">{option.name}</span>
      {/* Subtle key treatment — matches the admin partners list (partners-list.tsx). */}
      <span className="text-muted-foreground font-mono text-xs">{option.key}</span>
      {active ? (
        <HugeiconsIcon icon={Tick02Icon} strokeWidth={2} className="ms-auto size-4" />
      ) : null}
    </CommandItem>
  );
}

/**
 * The filterable partner list. Exported so it can be rendered and tested on its
 * own without opening a Radix submenu. `ChangePartnerMenu` wraps this in the
 * fan-out.
 */
export function ChangePartnerCommand({
  partners,
  activePartnerKey,
  articPartnerKey,
  lastPartnerKey,
  onSelect,
}: ChangePartnerMenuProps) {
  const [search, setSearch] = React.useState('');

  const sorted = React.useMemo(
    () => [...partners].sort((a, b) => a.name.localeCompare(b.name)),
    [partners],
  );
  const byKey = React.useMemo(() => new Map(partners.map((p) => [p.key, p])), [partners]);

  const articOption = articPartnerKey ? byKey.get(articPartnerKey) : undefined;
  const lastOption =
    lastPartnerKey && lastPartnerKey !== articPartnerKey ? byKey.get(lastPartnerKey) : undefined;

  // Pins are shortcuts shown only when not searching; the full alphabetical list
  // below always contains every partner (including the pinned ones).
  const showPins = search.trim() === '' && (articOption != null || lastOption != null);

  return (
    <Command
      filter={(_value, query, keywords) => {
        if (!query) return 1;
        const haystack = (keywords ?? []).join(' ').toLowerCase();
        return haystack.includes(query.toLowerCase()) ? 1 : 0;
      }}
    >
      <CommandInput
        autoFocus
        value={search}
        onValueChange={setSearch}
        placeholder="Filter by name or key…"
      />
      <CommandList>
        <CommandEmpty>No partners found.</CommandEmpty>
        {showPins ? (
          <>
            <CommandGroup>
              {articOption ? (
                <PartnerCommandItem
                  option={articOption}
                  value={`pin:${articOption.key}`}
                  active={articOption.key === activePartnerKey}
                  onSelect={onSelect}
                />
              ) : null}
              {lastOption ? (
                <PartnerCommandItem
                  option={lastOption}
                  value={`pin:${lastOption.key}`}
                  active={lastOption.key === activePartnerKey}
                  onSelect={onSelect}
                />
              ) : null}
            </CommandGroup>
            <CommandSeparator />
          </>
        ) : null}
        <CommandGroup>
          {sorted.map((partner) => (
            <PartnerCommandItem
              key={partner.key}
              option={partner}
              value={partner.key}
              active={partner.key === activePartnerKey}
              onSelect={onSelect}
            />
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}

/**
 * Admin-only "Change Partner" fan-out for the NavUser dropdown. Renders a
 * submenu with a quick filter (name + key), pinned Artic + last-viewed partner,
 * and the full alphabetical partner list. Presentational only — the consumer
 * supplies the data and handles the switch in `onSelect`.
 */
export function ChangePartnerMenu(props: ChangePartnerMenuProps) {
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <HugeiconsIcon icon={Building03Icon} strokeWidth={2} />
        Change Partner
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="w-64 p-0">
        {/*
         * Keep every key except Escape inside cmdk: the dropdown menu runs its
         * own typeahead on printable keys, which would otherwise hijack the
         * filter input. Stopping propagation here (below the SubContent's own
         * handler) prevents that while leaving cmdk's arrow-key navigation and
         * Escape-to-close intact.
         */}
        <div
          onKeyDown={(e) => {
            if (e.key !== 'Escape') e.stopPropagation();
          }}
        >
          <ChangePartnerCommand {...props} />
        </div>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}
